import metricMetadataIcon from '../assets/metric-metadata-icon-white.svg';
import { useEffect, useRef, useState } from "react";
import MetricMetadataModal from "./MetricMetadataModal";
import { DisaggregatedMetric, DistributionData, GeoDetails, GeoTag, Metric, MetricMetadata, RawIndicatorData } from "../services/types";
import { fetchDistributionData, fetchIndicatorBinCount, fetchMetricMetadata } from "../utils/dashboardUtils";
import IconTooltip from "./IconTooltip";
import { formatValue } from '../utils/helpers';

interface DisaggregateMetricCardProps {
    id: number;
    metric: Metric | null;
    title: string;
    disaggData: DisaggregatedMetric[] | null;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    selectionActive: boolean;
    svg?: string;
    showMetadataIcon?: boolean;
    showValue: boolean;
    showYear?: boolean;
};

const DisaggregateMetricCard = ({ id, metric, title, disaggData, geoDetails, geoTag, selectionActive, svg, showMetadataIcon = true, showValue, showYear = false }: DisaggregateMetricCardProps) => {
    const [showMetadata, setShowMetadata] = useState(false);
    const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
    const [distributionData, setDistributionData] = useState<RawIndicatorData[] | null>(null);
    const [countyDistributionData, setCountyDistributionData] = useState<DistributionData[] | null>(null);
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [edgeProximity, setEdgeProximity] = useState<{ top: boolean, right: boolean, bottom: boolean, left: boolean }>({
        top: false,
        right: false,
        bottom: false,
        left: false
    });

    const tooltipRef = useRef<HTMLDivElement>(null);

    const handleClick = async () => {
        setShowMetadata((prev) => !prev);

        if (metadata && distributionData && countyDistributionData) {
            // Skip fetching if all data is already present
            return;
        }
        
        setLoading(true);

        const fetchMetadata = metadata ? Promise.resolve({ data: metadata }) : fetchMetricMetadata(id);
        const fetchDistData = distributionData ? Promise.resolve(distributionData) : fetchIndicatorBinCount(id, geoDetails, geoTag);
        const fetchCountyDist = countyDistributionData ? Promise.resolve(countyDistributionData) : fetchDistributionData(id, [], null);

        try {
            const [metadataResponse, distributionDataResponse, countyDistributionDataResponse] = await Promise.all([
                fetchMetadata,
                fetchDistData,
                fetchCountyDist,
            ]);

            if (!metadata) setMetadata(metadataResponse?.data || null);
            if (!distributionData) setDistributionData(distributionDataResponse || null);
            if (!countyDistributionData) setCountyDistributionData(countyDistributionDataResponse || null);

        } catch (error) {
            const errorMessage = (error as Error).message || 'An unexpected error occurred.';
            console.error('Error fetching data:', error);
            setError(new Error(`Failed to load data: ${errorMessage}`)); // Pass detailed error message
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (tooltipRef.current) {
            const tooltipRect = tooltipRef.current.getBoundingClientRect();
            const proximity = {
                top: tooltipRect.top < 30,
                right: window.innerWidth - tooltipRect.right < 90,
                bottom: window.innerHeight - tooltipRect.bottom < 200,
                left: tooltipRect.left < 50
            };
            setEdgeProximity(proximity);
        }
    }, []);

    // define metric id, value and unit
    const { value, unit, unit_descriptor, year } = metric?.versions?.[0] || {};

    return (
        <div className={`rounded-t-md bg-lacey-blue`}>
            <div className="flex items-center justify-between px-2 h-8">
                <div className='flex items-center space-x-2'>
                    <p className="text-white text-xs font-bold">{title}</p>
                    
                    {/* Optionally show value for disaggregate header */}
                    {showValue && value && unit &&
                        <h2 className="text-white text-xs font-bold">{formatValue(value, unit, id)}</h2>
                    }
                </div>

                {showMetadataIcon && 
                    <div ref={tooltipRef} className="h-4 w-3 mb-1">
                        <button onClick={handleClick}>
                            <img src={metricMetadataIcon} alt="Metric metadata icon"/>
                        </button>
                        <IconTooltip edgeProximity={edgeProximity}/>
                    </div>
                }
            </div>
            {unit_descriptor && <p className="pb-2 px-2 -mt-1 text-white text-xs w-full">{unit_descriptor}</p>}
            {showYear && year && <p className="pb-2 px-2 -mt-1 text-white text-xs w-full">in {year}</p>}
            

            <MetricMetadataModal
                isOpen={showMetadata}
                onClose={handleClick}
                id={id}
                metadata={metadata}
                disaggData={disaggData}
                stateData={null}
                nationalData={null}
                distributionData={distributionData}
                countyDistributionData={countyDistributionData}
                selectionActive={selectionActive}
                svg={svg}
                loading={loading}
                error={error}
            />
        </div>
    );
};


export default DisaggregateMetricCard;
